import { useParams, Link } from 'react-router-dom';
import { getMovieById, getRelatedMovies } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';
import Breadcrumb from '@/components/feature/Breadcrumb';

function RelatedMovieCard({ movie }: { movie: Movie }) {
  return (
    <Link
      to={`/article/${movie.id}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-[2/3] overflow-hidden">
        <img
          src={movie.poster}
          alt={movie.title}
          title={movie.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-2 right-2 bg-primary-500 text-background-50 text-xs font-bold px-2 py-1 rounded-md whitespace-nowrap">
          {movie.rating}
        </div>
      </div>
      <div className="p-3">
        <h4 className="text-sm font-semibold text-foreground-900 truncate">{movie.title}</h4>
        <p className="text-xs text-foreground-500 mt-1">{movie.categoryName} · {movie.year}</p>
      </div>
    </Link>
  );
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movie = id ? getMovieById(id) : undefined;
  const relatedMovies = id ? getRelatedMovies(id) : [];

  if (!movie) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-background-50">
        <div className="text-center">
          <i className="ri-emotion-sad-line text-6xl text-foreground-300 mb-4 block"></i>
          <h1 className="text-xl font-bold text-foreground-900 mb-2">电影未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">抱歉，该电影资源不存在或已下架。</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap">
            <i className="ri-arrow-left-line"></i> 返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-background-50">
      {/* Breadcrumb */}
      <div className="w-full px-4 md:px-6 py-4 bg-background-100 border-b border-background-200">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: movie.categoryName, href: `/category/${movie.category}` },
            { label: movie.title },
          ]}
        />
      </div>

      {/* Movie Detail */}
      <article className="w-full px-4 md:px-6 py-8 md:py-10">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Poster */}
          <div className="flex-shrink-0 w-full lg:w-[300px]">
            <div className="aspect-[2/3] rounded-lg overflow-hidden">
              <img
                src={movie.poster}
                alt={movie.title}
                title={`${movie.title} - 极速影视高清电影大全`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Info */}
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              {movie.title}
            </h1>

            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary-100 text-primary-700 text-sm font-semibold rounded-full whitespace-nowrap">
                <i className="ri-star-fill text-xs"></i> {movie.rating}
              </span>
              <span className="px-3 py-1.5 bg-secondary-100 text-secondary-700 text-sm rounded-full whitespace-nowrap">
                {movie.categoryName}
              </span>
              <span className="px-3 py-1.5 bg-accent-100 text-accent-700 text-sm rounded-full whitespace-nowrap">
                {movie.year}
              </span>
              {movie.isNew && (
                <span className="px-3 py-1.5 bg-accent-500 text-background-50 text-sm rounded-full whitespace-nowrap">
                  最新上映
                </span>
              )}
              {movie.isHot && (
                <span className="px-3 py-1.5 bg-primary-500 text-background-50 text-sm rounded-full whitespace-nowrap">
                  热门推荐
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-foreground-500 w-16 flex-shrink-0">导演</span>
                <span className="text-foreground-800 font-medium">{movie.director}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-foreground-500 w-16 flex-shrink-0">时长</span>
                <span className="text-foreground-800">{movie.duration}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-foreground-500 w-16 flex-shrink-0">上映</span>
                <span className="text-foreground-800">{movie.releaseDate}</span>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-sm font-semibold text-foreground-500 mb-2">主演</h3>
              <div className="flex flex-wrap gap-2">
                {movie.actors.map((actor) => (
                  <span key={actor} className="px-3 py-1.5 bg-background-100 text-foreground-700 text-sm rounded-full whitespace-nowrap">
                    {actor}
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-sm font-semibold text-foreground-500 mb-2">剧情简介</h3>
              <p className="text-sm text-foreground-700 leading-relaxed">
                {movie.description}
              </p>
            </div>

            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-circle-line text-lg"></i>
              立即观看
            </Link>
          </div>
        </div>
      </article>

      {/* Related Movies */}
      {relatedMovies.length > 0 && (
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="mb-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
              相关推荐
            </h2>
            <p className="text-sm text-foreground-500 mt-1">喜欢{movie.categoryName}类电影的观众也在看</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
            {relatedMovies.map((m) => (
              <RelatedMovieCard key={m.id} movie={m} />
            ))}
          </div>
        </section>
      )}

      {/* SEO section */}
      <section className="w-full px-4 md:px-6 py-10 bg-background-50">
        <div className="max-w-3xl">
          <h2 className="text-lg font-bold text-foreground-900 mb-3">
            极速影视 - {movie.title}高清在线观看
          </h2>
          <p className="text-sm text-foreground-600 leading-relaxed">
            《{movie.title}》是极速影视高清电影大全收录的热门{movie.categoryName}电影，由{movie.director}执导，{movie.actors.join('、')}等主演。
            极速影视提供{movie.title}免费在线观看，高清不缓冲，畅享极致观影。极速影视最新院线同步更新{movie.categoryName}类高清资源，欢迎在线观看更多精彩电影。
          </p>
        </div>
      </section>

      {/* Schema.org */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Movie",
            "name": movie.title,
            "description": movie.description,
            "director": {
              "@type": "Person",
              "name": movie.director
            },
            "actor": movie.actors.map((a) => ({
              "@type": "Person",
              "name": a
            })),
            "dateCreated": movie.releaseDate,
            "inLanguage": "zh-CN",
            "genre": [movie.categoryName],
            "url": `https://www.jisumovies.com/article/${movie.id}`,
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "首页",
                  "item": "https://www.jisumovies.com/"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": movie.categoryName,
                  "item": `https://www.jisumovies.com/category/${movie.category}`
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": movie.title
                }
              ]
            }
          }),
        }}
      />
    </div>
  );
}